import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Save, Upload, RotateCcw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  fetchCommercialPlansCatalog,
  saveCommercialPlansDraft,
  fetchCommercialPlansImpact,
  publishCommercialPlans,
  rollbackCommercialPlans,
  fetchPlatformModules,
} from "@/lib/platformApi";
import { toast } from "sonner";

const PLAN_IDS = ["basic", "professional"];
const PLAN_LABELS = { basic: "Básico", professional: "Profesional" };
const MODULE_IDS = ["visor", "laboral", "tramites", "despacho"];
const MODULE_LABELS = {
  visor: "Visor SAT / CFDI",
  laboral: "Asistente Laboral",
  tramites: "Trámites Fiscales",
  despacho: "Gestión Despacho",
};

function emptyMatrix() {
  return {
    basic: {
      id: "basic",
      label: "Básico",
      entitlements: { visor: true, tramites: true, laboral: false, despacho: false },
      limits: { authorized_users: 3, authorized_clients: 25 },
      pricing: {
        monthly: { amount_minor: 99000, currency: "MXN" },
        annual: { amount_minor: 990000, currency: "MXN" },
      },
    },
    professional: {
      id: "professional",
      label: "Profesional",
      entitlements: { visor: true, tramites: true, laboral: true, despacho: true },
      limits: { authorized_users: 10, authorized_clients: 100 },
      pricing: {
        monthly: { amount_minor: 199000, currency: "MXN" },
        annual: { amount_minor: 1990000, currency: "MXN" },
      },
    },
  };
}

function pesosFromMinor(minor) {
  return (Number(minor || 0) / 100).toFixed(2);
}

function minorFromPesos(pesos) {
  const n = parseFloat(String(pesos || "0").replace(",", ""));
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}

export default function PlatformModules() {
  const [catalog, setCatalog] = useState(null);
  const [moduleItems, setModuleItems] = useState([]);
  const [plans, setPlans] = useState(emptyMatrix());
  const [draftId, setDraftId] = useState(null);
  const [publishedRev, setPublishedRev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [impact, setImpact] = useState(null);
  const [rollbackRev, setRollbackRev] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, mods] = await Promise.all([
        fetchCommercialPlansCatalog(),
        fetchPlatformModules(),
      ]);
      setCatalog(cat);
      setModuleItems(mods || []);
      const source = cat?.draft?.plans || cat?.published?.plans || emptyMatrix();
      setPlans({ ...emptyMatrix(), ...source });
      setDraftId(cat?.draft?.id || null);
      setPublishedRev(cat?.published?.revision_number || null);
    } catch {
      toast.error("No se pudo cargar la matriz de planes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateEntitlement = (planId, modId, checked) => {
    setPlans((prev) => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        entitlements: { ...prev[planId].entitlements, [modId]: checked },
      },
    }));
  };

  const updateLimit = (planId, key, value) => {
    setPlans((prev) => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        limits: { ...prev[planId].limits, [key]: parseInt(value, 10) || 0 },
      },
    }));
  };

  const updatePrice = (planId, cadence, pesos) => {
    setPlans((prev) => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        pricing: {
          ...prev[planId].pricing,
          [cadence]: {
            ...prev[planId].pricing[cadence],
            amount_minor: minorFromPesos(pesos),
            currency: "MXN",
          },
        },
      },
    }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const draft = await saveCommercialPlansDraft({ plans });
      setDraftId(draft.id);
      toast.success("Borrador guardado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.errors?.[0] || "Error al guardar borrador");
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewImpact = async () => {
    if (!draftId) {
      toast.error("Guarde el borrador antes de ver el impacto");
      return;
    }
    try {
      const data = await fetchCommercialPlansImpact(draftId);
      setImpact(data);
    } catch {
      toast.error("No se pudo calcular el impacto");
    }
  };

  const handlePublish = async () => {
    if (!draftId) {
      toast.error("Guarde el borrador antes de publicar");
      return;
    }
    const affected = Number(impact?.licenses_with_module_changes || 0);
    const confirmation = affected
      ? `Esta publicación reemitirá ${affected} licencia(s) web activa(s) con los nuevos módulos y límites. ¿Desea continuar?`
      : "Publicará esta matriz como la versión vigente de los planes. ¿Desea continuar?";
    if (!window.confirm(confirmation)) return;
    setPublishing(true);
    try {
      await publishCommercialPlans({ draft_id: draftId, confirm_propagation: true });
      toast.success("Plan publicado y propagado a licencias activas");
      setImpact(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "PLAN_PUBLISH_NEEDS_CONFIRM") {
        setImpact(detail.impact);
        toast.warning("Confirme la propagación: afectará licencias activas");
      } else {
        toast.error("Error al publicar");
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleRollback = async () => {
    const rev = parseInt(rollbackRev, 10);
    if (!rev) return;
    if (!window.confirm(`La restauración creará una nueva revisión y propagará la matriz de la revisión ${rev}. ¿Desea continuar?`)) {
      return;
    }
    setPublishing(true);
    try {
      await rollbackCommercialPlans({ revision_number: rev, confirm_propagation: true });
      toast.success(`Restauración a la revisión ${rev} completada`);
      await load();
    } catch {
      toast.error("Error al restaurar la revisión");
    } finally {
      setPublishing(false);
    }
  };

  const hasDraftChanges = useMemo(() => Boolean(catalog?.draft), [catalog]);

  if (loading) {
    return (
      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando matriz de planes…
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm space-y-6">
      <div>
        <h2 className="text-base font-semibold text-[#0F2D5C]">Planes comerciales Básico / Profesional</h2>
        <p className="text-sm text-slate-500 mt-1">
          Matriz editable. Al publicar con propagación, todas las licencias web activas se reemiten
          conservando vigencia y suscripción.
          {publishedRev ? ` Revisión publicada: v${publishedRev}.` : ""}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2 pr-3">Opción</th>
              {PLAN_IDS.map((pid) => (
                <th key={pid} className="py-2 px-3 text-center">{PLAN_LABELS[pid]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MODULE_IDS.map((mid) => (
              <tr key={mid} className="border-b border-slate-50">
                <td className="py-2 pr-3">{MODULE_LABELS[mid]}</td>
                {PLAN_IDS.map((pid) => (
                  <td key={`${pid}-${mid}`} className="py-2 px-3 text-center">
                    <Checkbox
                      checked={Boolean(plans[pid]?.entitlements?.[mid])}
                      onCheckedChange={(v) => updateEntitlement(pid, mid, Boolean(v))}
                    />
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-b border-slate-50">
              <td className="py-2 pr-3">Usuarios autorizados</td>
              {PLAN_IDS.map((pid) => (
                <td key={`${pid}-users`} className="py-2 px-3">
                  <Input
                    type="number"
                    min={1}
                    className="h-8 text-center"
                    value={plans[pid]?.limits?.authorized_users ?? ""}
                    onChange={(e) => updateLimit(pid, "authorized_users", e.target.value)}
                  />
                </td>
              ))}
            </tr>
            <tr className="border-b border-slate-50">
              <td className="py-2 pr-3">Clientes autorizados</td>
              {PLAN_IDS.map((pid) => (
                <td key={`${pid}-clients`} className="py-2 px-3">
                  <Input
                    type="number"
                    min={1}
                    className="h-8 text-center"
                    value={plans[pid]?.limits?.authorized_clients ?? ""}
                    onChange={(e) => updateLimit(pid, "authorized_clients", e.target.value)}
                  />
                </td>
              ))}
            </tr>
            <tr className="border-b border-slate-50">
              <td className="py-2 pr-3">Precio mensual (MXN)</td>
              {PLAN_IDS.map((pid) => (
                <td key={`${pid}-pm`} className="py-2 px-3">
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    className="h-8 text-center"
                    value={pesosFromMinor(plans[pid]?.pricing?.monthly?.amount_minor)}
                    onChange={(e) => updatePrice(pid, "monthly", e.target.value)}
                  />
                </td>
              ))}
            </tr>
            <tr className="border-b border-slate-50">
              <td className="py-2 pr-3">Precio anual (MXN)</td>
              {PLAN_IDS.map((pid) => (
                <td key={`${pid}-pa`} className="py-2 px-3">
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    className="h-8 text-center"
                    value={pesosFromMinor(plans[pid]?.pricing?.annual?.amount_minor)}
                    onChange={(e) => updatePrice(pid, "annual", e.target.value)}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {impact ? (
        <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold m-0">Impacto de publicación</p>
            <p className="m-0 mt-1">
              {impact.licenses_with_module_changes} de {impact.active_licenses_count} licencias activas
              cambiarán módulos incluidos.
            </p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Guardar borrador
        </Button>
        <Button type="button" variant="outline" onClick={handlePreviewImpact} disabled={!draftId}>
          Ver impacto
        </Button>
        <Button type="button" onClick={handlePublish} disabled={publishing || !hasDraftChanges}>
          <Upload className="h-4 w-4 mr-1" /> Publicar y propagar
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Input
            placeholder="N.º de revisión"
            className="h-8 w-24"
            value={rollbackRev}
            onChange={(e) => setRollbackRev(e.target.value)}
          />
          <Button type="button" variant="ghost" size="sm" onClick={handleRollback} disabled={publishing}>
            <RotateCcw className="h-4 w-4 mr-1" /> Restaurar
          </Button>
        </div>
      </div>

      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer font-medium text-slate-600">Catálogo técnico de módulos ({moduleItems.length})</summary>
        <ul className="mt-2 space-y-1 list-disc pl-4">
          {moduleItems.map((m) => (
            <li key={m.id}>
              <span className="font-mono">{m.id}</span> — {m.label}
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
